import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Container, Typography, Box, Card, CardContent, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, List, ListItem, ListItemText } from '@mui/material';
import { format } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';

// Register the components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const TimestampDetails = () => {
    const { websiteName, timestamp } = useParams();
    const [websiteStats, setWebsiteStats] = useState(null);
    const [comparisonData, setComparisonData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentField, setCurrentField] = useState('');
    const [dialogContent, setDialogContent] = useState('');

    const fetchWebsiteStats = useCallback(async () => {
        try {
            const response = await axios.get(`https://pixel-full-stack.onrender.com/api/get-website-stats/`, {
                params: {
                    website_name: websiteName,
                    timestamp: timestamp
                }
            });
            setWebsiteStats(response.data);
        } catch (error) {
            console.error('Error fetching website stats:', error);
        } finally {
            setLoading(false);
        }
    }, [websiteName, timestamp]);

    const fetchComparisonData = useCallback(async () => {
        try {
            const response = await axios.get(`https://pixel-full-stack.onrender.com/api/get-website-comparison/`, {
                params: {
                    website_name: websiteName
                }
            });
            setComparisonData(response.data);
        } catch (error) {
            console.error('Error fetching comparison data:', error);
        }
    }, [websiteName]);

    useEffect(() => {
        fetchWebsiteStats();
        fetchComparisonData();
    }, [fetchWebsiteStats, fetchComparisonData]);

        const renderGraph = (field, dataKey = field) => {
        if (!comparisonData.length) return null;
    
        const data = {
            labels: comparisonData.map(item => format(new Date(item.timestamp), 'PPpp')),
            datasets: [
                {
                    label: `${field} over time`,
                    data: comparisonData.map(item => {
                        const value = dataKey.includes('advanced_matching') ? item.advanced_matching[field] : item[dataKey];
                        return value === true ? 1 : value === false ? 0 : value;
                    }),
                    borderColor: 'rgba(75,192,192,1)',
                    backgroundColor: 'rgba(75,192,192,0.2)',
                    fill: true,
                    tension: 0.4,
                },
            ],
        };
    
        console.log(`Rendering graph for field: ${field}`);
        console.log('Data:', data);
    
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `${field} over time`,
                },
            },
        };
    
        return (
            <Box sx={{ height: 300, mb: 4 }}>
                <Line data={data} options={options} />
            </Box>
        );
    };

    const formatFieldName = (field) => {
        return field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const formattedTimestamp = format(new Date(timestamp), 'PPpp');

    // Specify the fields to display in the comparison graphs
    const fieldsToDisplay = ['core_setup', 'first_party_cookies', 'micro_data', 'gating', 'automatic_setup', 'micro_data_json_ld'];

    // Map containing information text for each field
    const fieldInfoMap = {
        core_setup: 'Information about core setup.',
        first_party_cookies: 'Information about first party cookies.',
        micro_data: 'Information about micro data.',
        gating: 'Information about gating.',
        automatic_setup: 'Information about automatic setup.',
        micro_data_json_ld: 'Information about micro data JSON-LD.',
    };

    // Map containing information text for each graph
    const graphInfoMap = {
        core_setup: 'Graph information about core setup.',
        first_party_cookies: 'Graph information about first party cookies.',
        micro_data: 'Graph information about micro data.',
        gating: 'Graph information about gating.',
        automatic_setup: 'Graph information about automatic setup.',
        micro_data_json_ld: 'Graph information about micro data JSON-LD.',
    };

    const handleOpenDialog = (field, content) => {
        setCurrentField(field);
        setDialogContent(content);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                Stats for {websiteName} at {formattedTimestamp}
            </Typography>
            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            ) : (
                websiteStats && (
                    <Box>
                        <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                            {Object.keys(websiteStats).map((field, index) => {
                                if (field === 'events' && (!websiteStats[field] || websiteStats[field].length === 0)) {
                                    return null;
                                }
                                if (field === 'advanced_matching') {
                                    return (
                                        <Card key={index} sx={{ minWidth: 275, flex: '1 1 calc(33% - 16px)' }}>
                                            <CardContent>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="h6" gutterBottom>
                                                        {formatFieldName(field)}
                                                    </Typography>
                                                    {field in fieldInfoMap && (
                                                        <IconButton onClick={() => handleOpenDialog(field, fieldInfoMap[field])}>
                                                            <InfoIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <List>
                                                    {Object.entries(websiteStats[field]).map(([key, value], idx) => (
                                                        <ListItem key={idx}>
                                                            <ListItemText primary={`${formatFieldName(key)}: ${value.toString()}`} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    );
                                }
                                return (
                                    <Card key={index} sx={{ minWidth: 275, flex: '1 1 calc(33% - 16px)' }}>
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="h6" gutterBottom>
                                                    {formatFieldName(field)}
                                                </Typography>
                                                {field in fieldInfoMap && (
                                                    <IconButton onClick={() => handleOpenDialog(field, fieldInfoMap[field])}>
                                                        <InfoIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            {field === 'events' ? (
                                                <List>
                                                    {websiteStats[field].map((event, idx) => (
                                                        <ListItem key={idx}>
                                                            <ListItemText primary={`${event.name}: ${event.value}`} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    {websiteStats[field].toString()}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                        <Typography variant="h5" gutterBottom>
                            Stats Comparison Over Time
                        </Typography>
                        {fieldsToDisplay.map((field, index) => (
                            <Card key={index} elevation={3} sx={{ p: 2, mb: 4 }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6" gutterBottom>
                                            {formatFieldName(field)}
                                        </Typography>
                                        <IconButton onClick={() => handleOpenDialog(field, graphInfoMap[field])}>
                                            <InfoIcon />
                                        </IconButton>
                                    </Box>
                                    {renderGraph(field)}
                                </CardContent>
                            </Card>
                        ))}
                        <Typography variant="h5" gutterBottom>
                            Advanced Matching Comparison
                        </Typography>
                        {comparisonData.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                                {Object.keys(comparisonData[0].advanced_matching).map((field, index) => (
                                    <Card key={index} elevation={3} sx={{ p: 2, mb: 4 }}>
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="h6" gutterBottom>
                                                    {formatFieldName(field)}
                                                </Typography>
                                                <IconButton onClick={() => handleOpenDialog(field, graphInfoMap[field])}>
                                                    <InfoIcon />
                                                </IconButton>
                                            </Box>
                                            {renderGraph(field, `advanced_matching.${field}`)}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        )}
                    </Box>
                )
            )}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{formatFieldName(currentField)}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogContent}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TimestampDetails;